/**
 * Persist lead-list filters for the signed-in user until they clear them.
 * Stored in localStorage under a per-user key so the same filters come back on the next login.
 */

const RETURN_PATH_KEY = "lead-list-return-path";

export type PersistedAllLeadsFilters = {
  search?: string;
  searchInput?: string;
  selectedStages?: string[];
  selectedSources?: string[];
  assignmentFilter?: string;
  formLead?: string[];
  selectedUploadLeadIds?: string[];
  selectedMemberIds?: string[];
  selectedDepartmentIds?: string[];
  selectedTeamIds?: string[];
  selectedDateRange?: { from?: string | null; to?: string | null };
  freshFilter?: boolean;
  page?: number;
  pageSize?: number;
};

export type PersistedMyLeadFilters = {
  selectedTab?: number;
  tabFilters?: Record<string, any>;
  page?: number;
  pageSize?: number;
  searchInput?: string;
};

export type PersistedTeamLeadFilters = {
  search?: string;
  searchInput?: string;
  /** Multi-select stage labels */
  selectedStages?: string[];
  /** @deprecated single stage — migrated to selectedStages */
  stage?: any;
  source?: any;
  selectedSources?: string[];
  formLead?: string[];
  selectedDateRange?: { from?: string | null; to?: string | null };
  selectedTeamFilter?: any;
  selectedMemberFilter?: any;
  selectedDepartmentIds?: string[];
  selectedTeamIds?: string[];
  selectedMemberIds?: string[];
  freshFilter?: boolean;
  attentionFilter?: boolean;
  untouchedDays?: number;
  page?: number;
  pageSize?: number;
};

function safeParse<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function scopedFilterKey(key: string, userId?: string) {
  const id = String(userId || "").replace(/['"]/g, "").trim();
  return id ? `${key}:${id}` : key;
}

export function loadLeadListFilters<T>(key: string, userId?: string): T | null {
  const scoped = scopedFilterKey(key, userId);
  try {
    const saved = safeParse<T>(localStorage.getItem(scoped));
    if (saved) return saved;
    const legacy = safeParse<T>(sessionStorage.getItem(key));
    if (legacy) {
      localStorage.setItem(scoped, JSON.stringify(legacy));
      return legacy;
    }
    return null;
  } catch {
    return null;
  }
}

export function saveLeadListFilters(key: string, value: unknown, userId?: string): void {
  try {
    localStorage.setItem(scopedFilterKey(key, userId), JSON.stringify(value));
  } catch {
    // ignore quota / private mode
  }
}

export function clearLeadListFilters(key: string, userId?: string): void {
  const scoped = scopedFilterKey(key, userId);
  try {
    localStorage.removeItem(scoped);
    sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
}

/**
 * When a parent pipeline stage is selected, include its sub-stages too
 * so filtering "Pipeline" also matches child stage names on leads.
 */
export function expandStageSelection(
  selected: string[] | undefined | null,
  stageTree: any[] | undefined | null
): string[] {
  const set = new Set(
    (selected || []).map((s) => String(s || "").trim()).filter(Boolean)
  );
  if (!set.size) return [];
  for (const stage of stageTree || []) {
    const parent = String(stage?.stage_Name || "").trim();
    if (!parent || !set.has(parent)) continue;
    for (const sub of stage?.sub_Stage_name || []) {
      const child = String(sub?.stage_Name || "").trim();
      if (child) set.add(child);
    }
  }
  return Array.from(set);
}

/** Remember which list page to return to from lead details. */
export function setLeadListReturnPath(path: string): void {
  try {
    if (path) sessionStorage.setItem(RETURN_PATH_KEY, path);
  } catch {
    // ignore
  }
}

export function getLeadListReturnPath(fallback: string): string {
  try {
    return sessionStorage.getItem(RETURN_PATH_KEY) || fallback;
  } catch {
    return fallback;
  }
}

/** Read page/pageSize from `?page=&pageSize=` (0-based page). */
export function parseListPagination(
  search: string,
  fallback: { page?: number; pageSize?: number } = {}
): { page: number; pageSize: number } {
  const params = new URLSearchParams(search || "");
  const pageRaw = params.get("page");
  const sizeRaw = params.get("pageSize") || params.get("limit");
  const pageParsed =
    pageRaw != null && pageRaw !== ""
      ? parseInt(pageRaw, 10)
      : NaN;
  const sizeParsed =
    sizeRaw != null && sizeRaw !== ""
      ? parseInt(sizeRaw, 10)
      : NaN;
  const page = Number.isFinite(pageParsed)
    ? Math.max(0, pageParsed)
    : Math.max(0, Number(fallback.page) || 0);
  const pageSize = Number.isFinite(sizeParsed)
    ? Math.min(200, Math.max(1, sizeParsed))
    : Math.min(200, Math.max(1, Number(fallback.pageSize) || 25));
  return { page, pageSize };
}

/**
 * Merge pagination into a list URL while keeping other query params
 * (e.g. bulkJobId). Omits page=0 / default pageSize=25 for cleaner URLs.
 */
export function withListPagination(
  pathname: string,
  page: number,
  pageSize: number,
  currentSearch?: string
): string {
  const params = new URLSearchParams(currentSearch || "");
  const safePage = Math.max(0, Number(page) || 0);
  const safeSize = Math.min(200, Math.max(1, Number(pageSize) || 25));
  if (safePage > 0) params.set("page", String(safePage));
  else params.delete("page");
  if (safeSize !== 25) params.set("pageSize", String(safeSize));
  else params.delete("pageSize");
  const qs = params.toString();
  return qs ? `${pathname}?${qs}` : pathname;
}

export const FILTER_KEYS = {
  allLeads: "lead-filters:all-leads",
  myLead: "lead-filters:my-lead",
  teamLeads: "lead-filters:team-leads",
} as const;
